import { FunctionComponent } from 'preact';
import { useTranslation } from 'react-i18next';
import { PudoLocation } from '@pperform/pp-data-mappers';
import { Hyperlink } from '../../DropOffCard.styles';
import { generateMapsUrl } from '../../utils';

type Address = PudoLocation['address'];
type Coordinates = Pick<PudoLocation, 'lat' | 'lng'>;

export interface DropOffAddressProps {
  address: Address;
  coordinates: Coordinates;
  variant: 'link' | 'text';
  displayText?: string;
}

const DropOffAddress: FunctionComponent<DropOffAddressProps> = ({
  address,
  coordinates,
  variant = 'link',
  displayText,
}): JSX.Element | null => {
  const { t } = useTranslation();

  if (!address) return null;

  const { full, line1, line2, postalCode, city } = address;
  const { lat, lng } = coordinates;

  // address does not have country, unlike full
  const formattedAddress = [line1, line2, postalCode, city]
    .filter(Boolean)
    .join(', ');
  if (!formattedAddress) return null;

  if (variant === 'text') {
    return <p>{formattedAddress}</p>;
  }

  const url = generateMapsUrl({
    address: full,
    coordinates: { lat, lng },
  });

  return (
    <Hyperlink
      data-testid="drop-off-address-hyperlink"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      aria-label={t('ALT_TEXT_OPEN_ADDRESS_IN_GOOGLE_MAPS', {
        address: formattedAddress,
      })}
    >
      {displayText || formattedAddress}
    </Hyperlink>
  );
};

export { DropOffAddress };
